import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, computed, effect, inject, signal } from '@angular/core';

export type ThemePreference = 'dark' | 'light' | 'system';
export type ResolvedTheme = 'dark' | 'light';

/** Same key and default as `src/theme-init.js`, which applies the theme before bootstrap. */
export const THEME_STORAGE_KEY = 'mtm-theme';
export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'dark';
export const THEME_PREFERENCES: readonly ThemePreference[] = ['dark', 'light', 'system'];

const LIGHT_QUERY = '(prefers-color-scheme: light)';

function isPreference(value: unknown): value is ThemePreference {
  return value === 'dark' || value === 'light' || value === 'system';
}

/**
 * Dark / light / system theme. The resolved theme is written to `data-theme` on <html>,
 * which switches every `--mtm-*` token (src/theme/_tokens.scss). The preference is
 * persisted in localStorage only (no server-side storage).
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly media = this.document.defaultView?.matchMedia?.(LIGHT_QUERY) ?? null;

  private readonly _preference = signal<ThemePreference>(this.readStoredPreference());
  private readonly _systemPrefersLight = signal(this.media?.matches ?? false);

  readonly preference = this._preference.asReadonly();
  readonly theme = computed<ResolvedTheme>(() => {
    const pref = this._preference();
    if (pref !== 'system') return pref;
    return this._systemPrefersLight() ? 'light' : 'dark';
  });

  constructor() {
    const onSystemChange = (e: MediaQueryListEvent) => this._systemPrefersLight.set(e.matches);
    this.media?.addEventListener?.('change', onSystemChange);
    inject(DestroyRef).onDestroy(() => this.media?.removeEventListener?.('change', onSystemChange));

    effect(() => this.document.documentElement.setAttribute('data-theme', this.theme()));
  }

  setPreference(pref: ThemePreference): void {
    this._preference.set(pref);
    try {
      this.document.defaultView?.localStorage.setItem(THEME_STORAGE_KEY, pref);
    } catch {
      // Storage unavailable: the choice still applies for this session.
    }
  }

  /** Cycles dark → light → system → dark (the three-state topbar button). */
  cycle(): void {
    const index = THEME_PREFERENCES.indexOf(this._preference());
    this.setPreference(THEME_PREFERENCES[(index + 1) % THEME_PREFERENCES.length]);
  }

  private readStoredPreference(): ThemePreference {
    try {
      const stored = this.document.defaultView?.localStorage.getItem(THEME_STORAGE_KEY);
      return isPreference(stored) ? stored : DEFAULT_THEME_PREFERENCE;
    } catch {
      return DEFAULT_THEME_PREFERENCE;
    }
  }
}
